import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer
} from 'react'
import { browserHistory } from 'react-router'

import { S3 } from '@aws-sdk/client-s3'

import _ from 'lodash'
import { constructQueryRequest, curryLogger, processResultItems } from '../containers/Kendra/utils'
import { curryEventWithIdAndQuery } from '../containers/Kendra/analytics'

import { apiFetchKendra } from '../containers/Kendra/fetchers'

import { AvailableSortingAttributesManager } from '../containers/Kendra/sorting/AvailableSortingAttributesManager'
import { SelectedSortingAttributeManager } from '../containers/Kendra/sorting/SelectedSortingAttributeManager'
import {
  awsConfiguration as awsConfig,
  facetConfiguration as defaultFacetConfig,
  resultsConfiguration as defaultResultsOptions
} from '../containers/Kendra/configuration'
import {
  API_OPERATIONS,
  QUERY_RESULT_TYPE
} from '../containers/Kendra/constants'
import { useAuth } from '../hooks'

/**
 * Initial state for the Kendra context.
 */
const initialState = {
  isKendraEnabled: false,
  indexId: null,
  dataTableName: null,
  filters: null,
  states: {},
  dataReady: false,
  searchResults: {},
  topResults: [],
  faqResults: [],
  docResults: [],
  extendedResults: [],
  currentPageNumber: 1,
  queryText: '',
  error: undefined,
  attributeTypeLookup: undefined,
  dataSourceNameLookup: undefined,
  index: undefined,
  resultsOptions: defaultResultsOptions,
  facetConfiguration: defaultFacetConfig,
  facetsOpen: defaultFacetConfig?.facetPanelDefaultOpen ?? false,
  availableSortingAttributes: AvailableSortingAttributesManager.empty(),
  selectedSortingAttribute: SelectedSortingAttributeManager.default(),
  suggestionsEnabled: true,
  suggestions: [],
  selectedFacets: {
    dateRangeMap: {
    },
    stringMap: {
    }
  },
}

/**
 * Reducer function to manage the state of the Kendra context.
 *
 * @param {initialState} state - The current state.
 * @param {Object} action - The action to be performed.
 * @param {string} action.type - The type of the action.
 * @param {initialState} action.payload - The payload of the action.
 * @returns {initialState} The new state.
 */
function kendraStateReducer(state, action) {
  switch (action.type) {
    case 'BATCH_UPDATE':
      return { ...state, ...action.payload }
    case 'SEARCH_RESULTS':
      return {
        ...state,
        searchResults: action.payload,
        topResults: action.payload.ResultItems.filter(
          result => result.Type === QUERY_RESULT_TYPE.ANSWER
        ),
        faqResults: action.payload.ResultItems.filter(
          result => result.Type === QUERY_RESULT_TYPE.QUESTION_ANSWER
        ),
        docResults: action.payload.ResultItems.filter(
          result => result.Type === QUERY_RESULT_TYPE.DOCUMENT
        )
      }
    case 'IS_KENDRA_ENABLED':
    case 'INDEX_ID':
    case 'DATA_TABLE_NAME':
    case 'FILTERS':
    case 'STATES':
    case 'DATA_READY':
    case 'EXTENDED_RESULTS':
    case 'CURRENT_PAGE_NUMBER':
    case 'QUERY_TEXT':
    case 'ERROR':
    case 'ATTRIBUTE_TYPE_LOOKUP':
    case 'DATA_SOURCE_NAME_LOOKUP':
    case 'INDEX':
    case 'RESULTS_OPTIONS':
    case 'FACET_CONFIGURATION':
    case 'FACETS_OPEN':
    case 'AVAILABLE_SORTING_ATTRIBUTES':
    case 'SELECTED_SORTING_ATTRIBUTE':
    case 'SUGGESTIONS_ENABLED':
    case 'SUGGESTIONS':
      return { ...state, [_.camelCase(action.type)]: action.payload }
    default:
      return state
  }
}

/**
 * Default value for the Kendra context.
 */
const kendraContextValue = {
  search: initialState,
  setSearch: () => {},
  isKendraEnabled: initialState.isKendraEnabled,
  getResults: () => new Promise(() => {}),
  kendra: () => new Promise(() => {}),
  getResultsHelper: () => new Promise(() => {}),
  pushEvent: () => {}
}

export const KendraContext = createContext(kendraContextValue)

const _loadingErrors = []

const { accessKeyId, secretAccessKey, region } = awsConfig

/**
 * Component to provide the Kendra context to its children.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.getOption - A function to get options.
 * @param {Object} props.user - The user object.
 * @param {Function} props.createEvent - A function to create an event.
 * @param {React.ReactNode} props.children - The child components.
 * @returns {JSX.Element} The Kendra context provider.
 */
function KendraContextProvider({ children, ...props }) {
  const { getOption, user, createEvent } = props
  const { loggedIn } = useAuth()

  /**
   * Initializes the search state and sets the search state reducer.
   *
   * @param {Function} kendraStateReducer - The reducer function to manage the state.
   * @param {Object} initialState - The initial state of the search.
   * @param {Function} getOption - A function to get redux/custOptions.
   * @param {Object} user - The user object.
   * @returns {Array} The search state and the dispatch function to update the state.
   */
  const [search, setSearch] = useReducer(
    kendraStateReducer,
    initialState,
    () => {
      const {
        indexId,
        dataTableName,
        showGenAIResult,
        showTopResults,
        showFAQResults,
        showDocumentResults,
        enableSorting,
        enablePagination,
        enablePageIndex,
        documentResultAnswerFontSize
      } = getOption('search.fivestar')
      const isKendraEnabled =
        user.careassist?.fivestar_enabled === 1 && indexId !== null
      return {
        ...initialState,
        isKendraEnabled,
        indexId,
        dataTableName,
        resultsOptions: {
          enableFacets: false,
          showGenAIResult,
          showTopResults,
          showFAQResults,
          showDocumentResults,
          enableSorting,
          enablePagination,
          enablePageIndex,
          documentResultsOptions: {
            answerFontSize: documentResultAnswerFontSize
          }
        }
      }
    }
  )

  /**
   * Creates an S3 client instance using AWS SDK if there are no loading errors.
   *
   * @returns {S3|undefined} The S3 client instance or undefined if there are loading errors.
   */
  const s3 = useMemo(() => {
    return !_loadingErrors.length
      ? new S3({
          region,
          credentials: { accessKeyId, secretAccessKey }
        })
      : undefined
  }, [])

  /**
   * Function to call the Kendra API.
   *
   * @param {string} operation - The API operation to perform.
   * @param {Object} params - The parameters for the API call.
   * @returns {Promise<Object|null>} The API response or null if an error occurs.
   */
  const kendra = useCallback(
    async (operation, params) => {
      try {
        const fetchParams = {
          params: {
            ...params,
            IndexId: search.indexId
          },
          region,
          indexId: params.indexId,
          operation
        }

        if (search.dataTableName) {
          fetchParams.dataTableName = search.dataTableName
        }

        return await apiFetchKendra(fetchParams)
      } catch (e) {
        console.error(
          `Error calling Kendra API: ${operation} with params: ${JSON.stringify(
            params
          )}`,
          e?.message || e
        )
        return null
      } finally {
        curryLogger(
          `[KENDRA] ${operation}`,
          `${JSON.stringify(
            { ...params, IndexId: search.indexId },
            null,
            2
          )}`

        )
      }
    },
    [search.indexId]
  )

  /**
   * Pusher tp create a kendrasearch-specific event for BigQuery.
   *
   * @param {Function} createEvent - The function to create an event.
   * @param {string} search.indexId - The ID of the search index.
   * @param {string} search.queryText - The search query text.
   * @returns {Function} The memoized function to create an event.
   */
  const pushEvent = useMemo(() => curryEventWithIdAndQuery(
    createEvent,
    search.indexId,
    search.queryText
  ), [createEvent, search.indexId, search.queryText])

  /**
   * Helper function to get search results.
   *
   * @param {Object} params - The parameters for the search query.
   * @param {string} params.queryText - The search query text.
   * @param {number} params.pageNumber - The page number.
   * @param {Object} [params.filter] - The filter for the search query.
   * @param {string} [params.sort] - The sort attribute for the search query.
   * @param {string} [params.order] - The sort order for the search query.
   * @returns {Promise<void>}
   */
  const getResultsHelper = async ({
    queryText,
    pageNumber,
    filter,
    sort,
    order
  }) => {
    setSearch({ type: 'DATA_READY', payload: false })

    const queryRequest = constructQueryRequest({
      indexId: search.indexId,
      queryText,
      pageNumber,
      filter,
      sort,
      order
      // type: search.resultsOptions.showDocumentResults
      //    ? QUERY_RESULT_TYPE.DOCUMENT
      //    : undefined,
      // pageSize: PAGE_SIZE
    })

    try {
      const results = await kendra(API_OPERATIONS.QUERY, queryRequest)
      if (search.resultsOptions.showGenAIResult) {
        const extendedResults = await kendra(
          API_OPERATIONS.RETRIEVE,
          queryRequest
        )
        setSearch({ type: 'EXTENDED_RESULTS', payload: extendedResults })
      }
      if (results?.ResultItems) {
        await processResultItems(results, s3)
        setSearch({ type: 'SEARCH_RESULTS', payload: results })
        setSearch({
          type: 'BATCH_UPDATE',
          payload: {
            dataReady: true,
            error: undefined,
            currentPageNumber: pageNumber,
            queryText
          }
        })
        pushEvent({
          action: 'success',
          search_term: queryText,
          outcome_type: 'search_results_count',
          outcome_value: results.TotalNumberOfResults
        })
      }
    } catch (e) {
      console.error('Error fetching data:', e)
      setSearch({
        type: 'BATCH_UPDATE',
        payload: {
          searchResults: {},
          dataReady: false,
          error: e
        }
      })
    }
  }

  /**
   * Function to get search results.
   * When submitting query from search bar, reset sorting attributes
   *
   * @param {string} queryText - The search query text.
   * @param {number} [pageNumber=1] - The page number.
   * @returns {Promise<void>}
   */
  const getResults = async (queryText, pageNumber = 1) => {
    setSearch({
      type: 'SELECTED_SORTING_ATTRIBUTE',
      payload: SelectedSortingAttributeManager.default()
    })
    return getResultsHelper({ queryText, pageNumber })
  }

  useEffect(() => {
    /**
     * Sets up a listener for browser history changes.
     * When the history changes, it resets the search query text, data readiness,
     * and selected sorting attribute.
     *
     * @returns {Function} A cleanup function to remove the listener.
     */
    const unlisten = browserHistory.listen(() => {
      setSearch({ type: 'QUERY_TEXT', payload: '' })
      setSearch({ type: 'DATA_READY', payload: false })
      setSearch({
        type: 'SELECTED_SORTING_ATTRIBUTE',
        payload: SelectedSortingAttributeManager.default()
      })
    })

    return () => {
      unlisten()
    }
  }, [])

  useEffect(() => {
    /**
     * Disable Kendra and reset index ID when the user is not logged in.
     *
     * @param {boolean} loggedIn - The logged-in status of the user.
     */
    if (!loggedIn) {
      setSearch({ type: 'IS_KENDRA_ENABLED', payload: false })
      setSearch({ type: 'INDEX_ID', payload: null })
      setSearch({ type: 'DATA_TABLE_NAME', payload: null })
    }
  }, [loggedIn])

  /**
   * Memoized value for the Kendra context.
   *
   * @param {Object} search - The current search state.
   * @param {Function} setSearch - The function to update the search state.
   * @param {boolean} search.isKendraEnabled - Indicates if Kendra is enabled.
   * @param {Function} kendra - The function to call the Kendra API.
   * @param {Function} getResults - The function to get search results.
   * @param {Function} getResultsHelper - The helper function to get search results.
   * @param {Function} pushEvent - The function to create an event.
   * @returns {Object} The memoized Kendra context value.
   */
  const kendraValue = useMemo(
    () => ({
      search,
      setSearch,
      isKendraEnabled: search.isKendraEnabled,
      kendra,
      getResults,
      getResultsHelper,
      pushEvent
    }),
    [search, setSearch, kendra, getResults, getResultsHelper, pushEvent]
  )

  return (
    <KendraContext.Provider value={kendraValue}>
      {children}
    </KendraContext.Provider>
  )
}

export default KendraContextProvider
